import React, { useState, useEffect, useCallback } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { signin } from "./api-auth.js";
import auth from "./../auth/auth-helper";
import { Navigate, Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLogout } from "../auth/actions/actions";
import { currentUser } from "../components/Usuario/api-usuario";
import { setUsuario } from "../components/Usuario/actions/actions";
import GooglePlay from "../shared/assets/img/disponivel-google-play-badge.png";
import AppStore from "../shared/assets/img/disponivel_app_store_2.png";
import { errorMessage } from "../util/ConfirmAlertUtil.js";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AuthLayout, { AuthFormHeading } from "./AuthLayout";

// Logo por ambiente/domínio. Fica fora do componente para não ser recriado
// a cada render, e é fácil de estender sem editar o componente.
const LOGO_BY_HOST = {
  "localhost:3000":
    "https://colesan-online-resources.s3.us-east-2.amazonaws.com/Logoblazon+Colesan+Online.png",
  "www.colesanonline.com.br":
    "https://colesan-online-resources.s3.us-east-2.amazonaws.com/Logoblazon+Colesan+Online.png",
};

function getLogoUrl() {
  return LOGO_BY_HOST[window.location.host] || "";
}

const STORE_LINKS = [
  {
    href: "https://play.google.com/store/apps/details?id=com.colesan",
    src: GooglePlay,
    alt: "Disponível no Google Play",
  },
  {
    href: "https://apps.apple.com/br/app/colesan-online/id1555752723",
    src: AppStore,
    alt: "Disponível na App Store",
  },
];

export default function SignInV3() {
  const [user, setUser] = useState({ username: "", password: "" });
  const [authenticated, setAuthenticated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const logoUrl = getLogoUrl();

  useEffect(() => {
    document.title = "Sign In";
  }, []);

  useEffect(() => {
    if (!auth.isAuthenticated()) return;

    setAuthenticated(true);

    const controller = new AbortController();
    currentUser({ signal: controller.signal })
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          dispatch(setUsuario(data));
        }
      })
      .catch((err) => {
        if (err.name !== "AbortError") console.error(err);
      });

    return () => controller.abort();
  }, [dispatch]);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  }, []);

  const clickSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (submitting) return; // evita duplo envio (duplo clique / Enter repetido)

      setSubmitting(true);
      try {
        const response = await signin(user);

        if (response.ok) {
          const jwtToken = response.headers.get("Authorization");
          if (!jwtToken) {
            toast.error("Erro", { position: "bottom-left" });
            return;
          }
          // Nota de segurança: guardar o JWT em localStorage expõe o token a
          // qualquer script rodando na página (XSS). Se possível, prefira um
          // cookie httpOnly emitido pelo backend. Mantido aqui para não
          // mudar o contrato com api-auth/auth-helper.
          localStorage.setItem("token", jwtToken);
          dispatch(setLogout(false));
          setAuthenticated(true);
          return;
        }

        // Tenta extrair uma mensagem de erro específica da API antes de
        // cair no genérico por status.
        let apiMessage = null;
        try {
          const body = await response.json();
          apiMessage = body?.message;
        } catch {
          /* corpo vazio ou não-JSON, segue com o fallback abaixo */
        }

        if (response.status === 403) {
          toast.error(apiMessage || "Acesso Negado", {
            position: "bottom-left",
          });
        } else {
          toast.error(apiMessage || "Erro", { position: "bottom-left" });
        }
      } catch (err) {
        if (err.message === "Failed to fetch") {
          toast.error("Ocorreu um erro. Tente novamente mais tarde.", {
            position: "bottom-left",
          });
        }
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    },
    [user, submitting, dispatch],
  );

  if (authenticated) {
    return <Navigate to="/home" />;
  }

  return (
    <AuthLayout
      eyebrow="Colesan Online"
      headline="Sua jornada acadêmica começa aqui."
      logoUrl={logoUrl}
      storeLinks={STORE_LINKS}
    >
      <ToastContainer autoClose={3000} />
      <AuthFormHeading
        title="Acessar sua conta"
        subtitle="Entre com suas credenciais do Portal Acadêmico."
      />

      <form onSubmit={clickSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          type="email"
          label="Email"
          name="username"
          autoComplete="email"
          autoFocus
          disabled={submitting}
          onChange={handleChange}
          value={user.username}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutlineIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Senha"
          type="password"
          id="password"
          autoComplete="current-password"
          disabled={submitting}
          onChange={handleChange}
          value={user.password}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : null
          }
          sx={{ mt: 3, py: 1.3 }}
        >
          {submitting ? "Acessando..." : "Acessar"}
        </Button>

        <Box sx={{ textAlign: "center", mt: 2.5 }}>
          <Link
            component={RouterLink}
            to="/request-reset-password"
            underline="hover"
            variant="body2"
            color="text.secondary"
          >
            Esqueci minha senha
          </Link>
        </Box>
      </form>
    </AuthLayout>
  );
}
